import { apiService } from './api';
import {
  Notification,
  NotificationsResponse,
  NotificationQuery,
  UnreadCountResponse,
  ApiResponse,
} from '@/types';

class NotificationService {
  /**
   * Get user notifications with pagination and filters
   */
  async getNotifications(
    query: NotificationQuery = {}
  ): Promise<ApiResponse<NotificationsResponse>> {
    try {
      const params = new URLSearchParams();

      if (query.page) params.append('page', query.page.toString());
      if (query.limit) params.append('limit', query.limit.toString());
      if (query.type) params.append('type', query.type);
      if (query.isRead !== undefined) params.append('isRead', query.isRead.toString());

      const queryString = params.toString();
      const url = `/auth/notifications${queryString ? `?${queryString}` : ''}`;

      return await apiService.get<NotificationsResponse>(url);
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch notifications',
        errors: error.response?.data?.errors,
      };
    }
  }

  /**
   * Get a specific notification by ID
   */
  async getNotificationById(id: string): Promise<ApiResponse<Notification>> {
    try {
      return await apiService.get<Notification>(`/auth/notifications/${id}`);
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch notification',
        errors: error.response?.data?.errors,
      };
    }
  }

  /**
   * Mark a notification as read
   */
  async markAsRead(id: string): Promise<ApiResponse<{}>> {
    try {
      return await apiService.put<{}>(`/auth/notifications/${id}/read`);
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to mark notification as read',
        errors: error.response?.data?.errors,
      };
    }
  }

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(): Promise<ApiResponse<{}>> {
    try {
      return await apiService.put<{}>('/auth/notifications/read-all');
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to mark all notifications as read',
        errors: error.response?.data?.errors,
      };
    }
  }

  /**
   * Get unread notification count
   */
  async getUnreadCount(): Promise<ApiResponse<UnreadCountResponse>> {
    try {
      return await apiService.get<UnreadCountResponse>('/auth/notifications/unread-count');
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch unread count',
        errors: error.response?.data?.errors,
      };
    }
  }

  /**
   * Delete a notification
   */
  async deleteNotification(id: string): Promise<ApiResponse<{}>> {
    try {
      return await apiService.delete<{}>(`/auth/notifications/${id}`);
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to delete notification',
        errors: error.response?.data?.errors,
      };
    }
  }

  /**
   * Delete all notifications
   */
  async deleteAllNotifications(): Promise<ApiResponse<{}>> {
    try {
      return await apiService.delete<{}>('/auth/notifications');
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to delete all notifications',
        errors: error.response?.data?.errors,
      };
    }
  }

  /**
   * Polling function to check for new notifications
   */
  async pollNotifications(lastCount: number): Promise<{ hasNew: boolean; count: number }> {
    try {
      const response = await this.getUnreadCount();
      if (response.success && response.data) {
        const currentCount = response.data.count;
        return {
          hasNew: currentCount > lastCount,
          count: currentCount,
        };
      }
      return { hasNew: false, count: lastCount };
    } catch (error) {
      console.error('Failed to poll notifications:', error);
      return { hasNew: false, count: lastCount };
    }
  }
}

export default new NotificationService();
